import React, { useState, useEffect } from "react";

import { XIcon } from "@heroicons/react/outline";

import { useFormik } from "formik";
import * as yup from "yup";

import Dialog from "@/ui/Dialog";
import TextFieldFormik from "@/ui/TextFieldFormik";
import { TextAreaFormik } from "@/ui/TextArea";
import Button from "@/ui/Button";
import { CheckboxFormik } from "@/ui/Checkbox";
import { readDocument, updateDocument, createEvent } from "@/lib/db";
import { useAuth } from "@/lib/auth";
import SignUpSignIn from "../user/SignUpSignIn";
import { LightButton, DarkButton } from "../community/CTACards";
import { IconButton } from "@/ui/IconButton";
import { YouNeedToRegister } from "./utils";
import { trackButtonClick } from "../user/UserUtils";

const updateLocalStorage = (user) => {
    const _name = localStorage.getItem("name");
    const _email = localStorage.getItem("email");
    const _phone = localStorage.getItem("phone");

    if (!_name && user.name) {
        localStorage.setItem("name", user.name);
    }

    if (!_email && user.email) {
        localStorage.setItem("email", user.email);
    }

    if (!_phone && user.phone) {
        localStorage.setItem("phone", user.phone);
    }
};

const readLocalStorage = () => {
    const name = localStorage.getItem("name");
    const email = localStorage.getItem("email");
    const phone = localStorage.getItem("phone");

    return { name, email, phone };
};

const RequestInfo = ({ community, email, phone }) => {
    const auth = useAuth();
    const [open, setOpen] = useState(false);
    const [openSignUp, setOpenSignUp] = useState(false);
    const [openSignIn, setOpenSignIn] = useState(false);
    const [user, setUser] = useState({});
    const [complete, setComplete] = useState(false);
    const [initialValues, setInitialValues] = useState({});

    const userId = auth && auth.user ? auth.user.uid : "";
    const token = auth && auth.user ? auth.user.token : "";

    useEffect(() => {
        const getUser = async (uid) => {
            const _user = await readDocument("users", uid);
            updateLocalStorage(_user);
            setUser(_user);
        };

        if (userId) {
            getUser(userId);
        }
    }, [userId]);

    useEffect(() => {
        const values = readLocalStorage();
        setInitialValues(values);
    }, []);

    const handleClose = () => {
        setOpen(false);
        setComplete(false);
    };

    const handleComplete = async ({ createAccount, values }) => {
        const res = await fetch("/api/email/request-info-unclaimed", {
            method: "post",
            headers: {
                "Content-Type": "application/json",
                token,
            },
            body: JSON.stringify(
                {
                    ...values,
                    title: `Request Info about ${community.name}`,
                    preview: `Request Info about ${community.name}`,
                },
                null,
                2
            ),
        });

        updateLocalStorage(values);

        if (createAccount) {
            const { name, email } = values;

            const password = ""; // will be generated randomly
            const receivedEmailUpdates = true;
            const emailLink = true;

            try {
                await auth.signUp(email, password, name, receivedEmailUpdates, emailLink);

                gtag("event", "conversion", {
                    send_to: "AW-10778046073/z0kvCMDQspMaEPncr5Mo",
                });

                oaiq("measure", "registration_completed", {
                    type: "customer_action",
                });
            } catch (err) {
                console.error("Error creating account", err);

                if (emailLink && err.code === "auth/email-already-in-use") {
                    // sign in the user with email link
                    await auth.signInWithUserEmailLink(email);
                }
            }
        }

        createEvent({
            nameId: community.nameId,
            communityName: community.name,
            uid: userId,
            name: user?.name ? user.name : "",
            email: user?.email ? user.email : email,
            phone: user?.phone ? user.phone : phone,
            type: "sent_request_info",
        });

        setComplete(true);
    };

    return (
        <div>
            <DarkButton
                onClick={() => {
                    const value = trackButtonClick("request_info_button");
                    gtag("event", "request_info", {
                        event_category: "engagement",
                        event_label: "request_info_button",
                        value,
                        item_id: community.nameId,
                    });

                    const engagementCount = trackButtonClick("engagement");
                    gtag("event", "engagementCountUpdated", {
                        engagement_count: engagementCount,
                    });

                    gtag("event", "conversion", {
                        send_to: "AW-10778046073/KDsKCIK6vpMaEPncr5Mo",
                    });

                    oaiq("measure", "lead_created", {
                        type: "customer_action",
                    });

                    createEvent({
                        nameId: community.nameId,
                        communityName: community.name,
                        uid: userId,
                        name: user?.name ? user.name : "",
                        email: user?.email ? user.email : "",
                        phone: user?.phone ? user.phone : "",
                        type: "clicked_request_info_button",
                    });

                    setOpen(true);
                }}
            >
                Request Info
            </DarkButton>

            <Dialog
                open={open}
                className="fixed inset-0 z-50 overflow-y-auto"
                onClose={handleClose}
            >
                <div className="min-h-screen px-4 text-center">
                    <Dialog.Overlay className="fixed -z-10 inset-0 bg-black opacity-70" />

                    {/* This element is to trick the browser into centering the modal contents. */}
                    <span className="inline-block h-screen align-middle" aria-hidden="true">
                        &#8203;
                    </span>

                    <div
                        className={`inline-block w-full max-w-lg pb-8 overflow-hidden
                                            text-left align-middle transition-all transform 
                                            bg-white shadow-xl rounded-2xl`}
                    >
                        <div className="flex justify-end"></div>

                        <Dialog.Title className="px-4 py-2 flex justify-between items-center bg-primary-500">
                            <div className="flex justify-center flex-grow">
                                <h3 className="text-2xl font-bold font-headline uppercase text-white">
                                    Request Info
                                </h3>
                            </div>
                            <IconButton onClick={handleClose}>
                                <XIcon className="w-6 h-6 text-white" />
                            </IconButton>
                        </Dialog.Title>

                        {/*userId === "" ? (
                            <YouNeedToRegister
                                setOpenSignIn={setOpenSignIn}
                                setOpenSignUp={setOpenSignUp}
                                onClose={handleClose}
                            />
                        ) : null*/}

                        {
                            /*userId !== "" &&*/ !complete ? (
                                <RequestInfoForm
                                    community={community}
                                    user={user}
                                    initialValues={initialValues}
                                    onComplete={handleComplete}
                                />
                            ) : null
                        }

                        {complete ? (
                            <RequestInfoComplete
                                community={community}
                                email={email}
                                phone={phone}
                            />
                        ) : null}

                        {!user?.uid ? (
                            <div className="px-8 mt-2 text-sm flex space-x-2">
                                <div className="flex items-center space-x-2">
                                    <div>Already have an account?</div>
                                    <buton
                                        className="font-bold text-primary-500 underline hover:text-primary-400 hover:cursor-pointer"
                                        onClick={() => {
                                            handleClose();
                                            setOpenSignIn(true);
                                        }}
                                    >
                                        Sign In
                                    </buton>
                                </div>

                                <div className="flex items-center space-x-2">
                                    <div>or</div>

                                    <buton
                                        className="font-bold text-primary-500 underline hover:text-primary-400 hover:cursor-pointer"
                                        onClick={() => {
                                            handleClose();
                                            setOpenSignUp(true);
                                        }}
                                    >
                                        Sign Up
                                    </buton>

                                    <div>to create an account.</div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </Dialog>

            <SignUpSignIn
                openSignUp={openSignUp}
                openSignIn={openSignIn}
                setOpenSignUp={setOpenSignUp}
                setOpenSignIn={setOpenSignIn}
            />
        </div>
    );
};

const validationSchema = yup.object({
    name: yup.string().trim().required("Name is required").nullable(),
    email: yup
        .string()
        .trim()
        .required("Email is required")
        .email("Please enter a valid email")
        .nullable(),
    phone: yup.string().trim().required("Mobile Number is required").nullable(),
    message: yup.string().trim().required("Message is required").nullable(),
});

const RequestInfoForm = ({ community, user, initialValues, onComplete }) => {
    // const [name, setName] = useState(user.name);
    // const [phone, setPhone] = useState(user.phone);
    // const [topic, setTopic] = useState("");
    // const [message, setMessage] = useState("");
    // const [urgent, setUrgent] = useState(false);

    const topics = [
        "Availability",
        "Cost + Affordability",
        "Qualifications",
        "Property + Services",
        "Tour Property",
        "Other",
    ].map((x) => ({ value: x, name: x }));

    const formik = useFormik({
        initialValues: {
            name: user.name ? user.name : initialValues.name,
            email: user.email ? user.email : initialValues.email,
            phone: user.phone ? user.phone : initialValues.phone,
            topic: "",
            message: "",
            urgent: false,
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            if (values.name !== user.name || values.phone !== user.phone) {
                try {
                    await updateDocument("users", user.uid, {
                        ...user,
                        name: values.name ? values.name : user.anme,
                        phone: values.phone ? values.phone : user.phone,
                    });
                } catch (err) {
                    console.error("Error updating user, maybe not logged in?", err);
                }
            }

            onComplete({
                createAccount: !user.uid,
                values: {
                    name: values.name,
                    email: values.email,
                    phone: values.phone,
                    topic: values.topic,
                    message: values.message,
                    urgent: values.urgent,
                    communityName: community.name,
                    communityUrl: community.nameId,
                },
            });
        },
    });

    return (
        <form onSubmit={formik.handleSubmit}>
            <div className="mt-0 sm:mt-6 mb-0 px-4 sm:px-8">
                <TextFieldFormik
                    formik={formik}
                    id="name"
                    name="name"
                    label="Full Name"
                    fullwidth
                    inputClassName="bg-gray-100"
                />

                <TextFieldFormik
                    formik={formik}
                    id="email"
                    type="email"
                    name="email"
                    label="Email"
                    fullwidth
                    className="mt-2"
                />

                <TextFieldFormik
                    formik={formik}
                    id="phone"
                    name="phone"
                    label="Mobile Number"
                    fullwidth
                    inputClassName="bg-gray-100"
                    className="mt-2"
                />

                <TextFieldFormik
                    formik={formik}
                    select
                    id="topic"
                    name="topic"
                    label="Info Topic"
                    options={topics}
                    fullwidth
                    inputClassName="bg-gray-100"
                    className="mt-2"
                />

                <TextAreaFormik
                    formik={formik}
                    id="message"
                    name="message"
                    label="Message"
                    fullwidth
                    inputClassName="bg-gray-100"
                    className="mt-2"
                    textareaClassName="bg-gray-100"
                />

                <div className="mt-2 flex justify-between">
                    <Button className="" type="submit">
                        Submit Request{user.uid ? "" : " and Create Account"}
                    </Button>
                    <CheckboxFormik
                        formik={formik}
                        id="urgent"
                        name="urgent"
                        label="Urgent Request"
                    />
                </div>
            </div>
        </form>
    );
};

const RequestInfoComplete = ({ community, email, phone }) => {
    const handlePrint = (e) => {
        e.preventDefault();
        e.stopPropagation();
        window.open("/community-mls/" + community.nameId, "_blank");
    };

    return (
        <div className="mt-6 mb-4 px-16">
            <p className="text-lg text-center">Thank you for your inquiry about</p>

            <p className="mt-2 text-xl text-primary-500 font-bold text-center">
                {community.name}
            </p>

            <p className="mt-4 text-base text-center">
                This community is not verified on Purple Door Finders.
            </p>
            <p className="mt-1 text-base text-center">
                You will need to reach out to them directly.
            </p>

            <div className="mt-4 px-2 py-2 bg-gray-200">
                <p className="text-base text-center">
                    Don't forget to download the community info sheet:
                </p>

                <a
                    className="text-primary-500 underline hover:font-bold text-base text-center flex justify-center"
                    href={"https://purpledoorfinders.com/community-mls/" + community.nameId}
                    onClick={handlePrint}
                >
                    Info Sheet
                </a>
            </div>

            {phone ? (
                <div className="mt-4">
                    <p className="text-base text-center">
                        Please call the community directly at:
                    </p>
                    <p className="text-center font-bold">{phone}</p>
                </div>
            ) : null}

            <p className="mt-4 text-sm font-bold text-center">
                Tell them Purple Door Finders sent you!
            </p>
        </div>
    );
};

export default RequestInfo;
